import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import {
  addInventoryService,
  updateInventoryService,
  getByIdInventoryService,
} from '@/services/inventoryService';
import { getItemService } from '@/services/ItemServices'; // ⬅️ reuse existing service
import { getItemCategoryService } from '@/services/ItemCategoryServices';

import SidebarDrawer from '@/components/common/SidebarDrawer';
import { Input } from '@/components/ui/inputs/input';
import InputRadio from '@/components/ui/inputs/input-radio';
import ToastMe from '@/components/ui/ToastMe';
import validationErrors from '@/lib/validationErrors';

export default function AddEditInventoryDrawer({
  activeDrawer,
  setActiveDrawer,
  getTableData,
  editDataId = '',
}) {
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      categoryFilter: '',
      item_id: '',
      item_category_id: '',
      quantity: 0,
      price: 0,
      status: 1,
    },
  });

  const [categories, setCategories]       = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);

  // 1) Load categories once drawer opens
  useEffect(() => {
    if (!activeDrawer) return;

    dispatch(getItemCategoryService({ search:'', page:1, limit:100, extra_filter:{} }))
      .then(res => {
        const opts = (res?.data?.data?.data || []).map(c => ({
          value: c._id,
          label: c.name + (c.isPerishable ? ' 🍃' : ''),
          isPerishable: !!c.isPerishable,
        }));
        setCategories(opts);
      })
      .catch(() => ToastMe('Failed to load categories','error'));
  }, [activeDrawer, dispatch]);

  // 2) Whenever categoryFilter changes, fetch items for that category
  const watchedCategory = watch('categoryFilter');
  useEffect(() => {
    if (!activeDrawer) return;

    // clear previous selection
    setValue('item_id','');
    setValue('item_category_id','');

    dispatch(getItemService({
      search: '',
      page: 1,
      limit: 100,
      extra_filter: {
        status: 1,
        item_category_id: watchedCategory || undefined,
        __enforceBranchStock: false, // ⬅️ show items even if qty=0 (for restocking)
        __forRestock: true,          // ⬅️ allow company-wide items when branch-scoped
      },
    }))
      .then((res) => {
        const arr = res?.data?.data?.data || [];
        const opts = arr.map(it => ({
          value: it._id,
          label: it.name,
          // support either id or embedded object from backend
          categoryId: it.item_category_id || it.item_category?._id || null,
        }));
        setFilteredItems(opts);
      })
      .catch(() => ToastMe('Failed to load items','error'));
  }, [activeDrawer, dispatch, watchedCategory, setValue]);

  // 3) If editing, populate fields
  useEffect(() => {
    if (activeDrawer && editDataId) {
      dispatch(getByIdInventoryService(editDataId))
        .then(res => {
          const inv = res?.data?.data;
          setValue('categoryFilter', inv?.item_category?._id || '');
          setValue('item_id',         inv?.item?._id || '');
          setValue('quantity',        inv?.quantity ?? 0);
          setValue('price',           inv?.price ?? 0);
          setValue('status',          inv?.status ?? 1);
        })
        .catch(() => ToastMe('Failed to load inventory','error'));
    } else {
      reset();
    }
  }, [activeDrawer, editDataId, dispatch, reset, setValue]);

  // 4) Sync item → item_category_id
  const watchedItem = watch('item_id');
  useEffect(() => {
    if (watchedItem) {
      const itm = filteredItems.find(i => i.value === watchedItem);
      if (itm?.categoryId) {
        setValue('item_category_id', itm.categoryId);
      }
    }
  }, [watchedItem, filteredItems, setValue]);

  const onSubmit = data => {
    const svc = editDataId
      ? updateInventoryService(editDataId, data)
      : addInventoryService(data);

    dispatch(svc)
      .then(() => {
        ToastMe('Inventory saved','success');
        closeDrawer();
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message));
  };

  const closeDrawer = () => {
    setActiveDrawer(false);
    getTableData();
    reset();
  };

  const isPerishable =
    categories.find(c => c.value === watchedCategory)?.isPerishable ?? true;

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={editDataId ? 'Edit Inventory' : 'Restock Inventory'}
      activeDrawer={activeDrawer}
      handleDrawerClose={closeDrawer}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* CATEGORY */}
        <Controller
          name="categoryFilter"
          control={control}
          rules={{ required: 'Category is required' }}
          render={({ field }) => (
            <div className="form-input-item">
              <label className="db-field-title required">Category</label>
              <Select
                options={categories}
                value={categories.find(c => c.value === field.value) || null}
                onChange={opt => field.onChange(opt ? opt.value : '')}
                placeholder="Select category"
                isClearable
              />
              {errors.categoryFilter && (
                <p className="text-red-600 text-sm">
                  {errors.categoryFilter.message}
                </p>
              )}
            </div>
          )}
        />

        {/* ITEM */}
        <Controller
          name="item_id"
          control={control}
          rules={{ required: 'Item is required' }}
          render={({ field }) => (
            <div className="form-input-item">
              <label className="db-field-title required">Item</label>
              <Select
                options={filteredItems}
                value={filteredItems.find(i => i.value === field.value) || null}
                onChange={opt => field.onChange(opt ? opt.value : '')}
                placeholder="Select item"
                isClearable
                isDisabled={!watchedCategory}
                noOptionsMessage={() =>
                  watchedCategory ? 'No items found in this category' : 'Select a category first'
                }
              />
              {errors.item_id && (
                <p className="text-red-600 text-sm">
                  {errors.item_id.message}
                </p>
              )}
            </div>
          )}
        />

        {/* QUANTITY */}
        <Controller
          name="quantity"
          control={control}
          rules={{
            required: 'Quantity is required',
            min: { value: 0, message: 'Minimum 0' },
          }}
          render={({ field }) => (
            <Input
              {...field}
              size="sm"
              type="number"
              labelText="Quantity"
              labelStyleClassName="db-field-title required"
              readOnly={!isPerishable}
              disabled={!isPerishable}
            />
          )}
        />
        {errors.quantity && (
          <p className="text-red-600 text-sm">{errors.quantity.message}</p>
        )}

        {/* PURCHASE PRICE */}
        <Controller
          name="price"
          control={control}
          rules={{
            required: 'Price is required',
            min: { value: 0, message: 'Minimum 0' },
          }}
          render={({ field }) => (
            <Input
              {...field}
              size="sm"
              type="number"
              step="0.01"
              labelText="Purchase Price"
              labelStyleClassName="db-field-title required"
            />
          )}
        />
        {errors.price && (
          <p className="text-red-600 text-sm">{errors.price.message}</p>
        )}

        {/* STATUS */}
        <div className="form-input-item">
          <label className="db-field-title required">Status</label>
          <Controller
            name="status"
            control={control}
            defaultValue={1}
            render={({ field }) => (
              <div className="db-field-radio-group">
                <InputRadio
                  id="statusActive"
                  labelText="Active"
                  value={1}
                  checked={field.value === 1}
                  onChange={() => field.onChange(1)}
                />
                <InputRadio
                  id="statusInactive"
                  labelText="Inactive"
                  value={2}
                  checked={field.value === 2}
                  onChange={() => field.onChange(2)}
                />
                <InputRadio
                  id="statusArchived"
                  labelText="Archived"
                  value={3}
                  checked={field.value === 3}
                  onChange={() => field.onChange(3)}
                />
              </div>
            )}
          />
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex gap-3 pt-4">
          <button type="submit" className="db-btn py-2 text-white bg-primary">
            <i className="lab lab-save" /> Save
          </button>
          <button
            type="button"
            className="modal-btn-outline"
            onClick={closeDrawer}
          >
            <i className="lab lab-close" /> Close
          </button>
        </div>
      </form>
    </SidebarDrawer>
  );
}
